import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getSlaGroupsAction } from 'store/underwriting/actions';
import { selectSlaGroupsOptions } from 'store/underwriting/selectors';

/**
 * Required SLA-group picker for a PromptUser condition-creating tool. Loads the
 * company's SLA groups (GET /SLAGroup) and reports the chosen group id via
 * onChange. The selected id is what the confirmation gate sends back as
 * selectedSlaGroupId. (DEV-3192)
 */
const SlaGroupSelect = ({
  onChange,
  value = null,
  disabled = false,
  error = false,
  helperText = '',
  label,
  placeholder,
  dataTestId,
}) => {
  const dispatch = useDispatch();
  const options = useSelector(selectSlaGroupsOptions);

  const selectedOption = useMemo(
    () => options.find(option => option.value === value) ?? null,
    [options, value],
  );

  const onSelect = useCallback(
    (_, option) => {
      onChange(option?.value ?? null);
    },
    [onChange],
  );

  useEffectSafeStrictMode(() => {
    if (options.length === 0) {
      dispatch(getSlaGroupsAction());
    }
  }, [dispatch, options.length]);

  return (
    <AutocompleteCDS
      dataTestId={dataTestId}
      disabled={disabled}
      error={error}
      helperText={helperText}
      label={label}
      name="slaGroup"
      optionName="name"
      options={options}
      placeholder={placeholder}
      value={selectedOption}
      onChange={onSelect}
    />
  );
};

SlaGroupSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  dataTestId: PropTypes.string,
};

export default memo(SlaGroupSelect);
